import React, { useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AuthenticationContextProviderProps, AuthenticationContext } from 'services/authentication/AuthenticationTypes';
import { useGlobalContext } from 'GlobalContext';
import { setCsrfToken } from './csrfFunctions';
import { login } from './loginFunction2';
import { verifyTwoFactorCode } from './twoFactorVerify';
import { switchEnvironment as switchEnvironmentRequest } from './switchEnvironment';
import { connectUpdatesSocket, disconnectUpdatesSocket, useSocketMessage } from 'services/api/updatesSocket';
import { logout } from './logoutFunction';
import { useLocation } from 'react-router-dom';
import { refreshStatesFromLocalStorage } from './refreshStates';
import { encryptData, getDecryptedLocalStorageItem } from "./encryptData";
import { loadTabSessionData } from './loadSessionData';
import { fetchSessionData } from './fetchSessionData';
import { getTabEnvironmentHash, getDefaultEnvironmentHash, setTabScope, clearTabScope, isTabOnDefaultEnvironment } from './environmentScope';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { getNavigatorLanguageOrFallback } from 'internationalization/i18n';

/*
 * AuthenticationContext.tsx
 * This context is created to contain states and information about the 
 * authenticated user and environments, such as login state but also 
 * environment features. The context also handles the initialization
 * of the csrf token, handling of the login, updates to the environment
 * features by websocket and logout functions.
 */ 

export const AuthenticationContextProvider: React.FC<AuthenticationContextProviderProps> = ({ children }) => {
    const currentSession = localStorage.getItem('currentSession') ? true : null;
    const [statesLoaded, setStatesLoaded] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(currentSession);
    const [userHash, setUserHash] = useState<string | undefined>(undefined);
    const [userDivision, setUserDivision] = useState<number | null>(null);
    const [firstName, setFirstName] = useState<string | null>(null);
    const [lastName, setLastName] = useState<string | null>(null);
    const [environmentHash, setEnvironmentHash] = useState<string>('');
    const [environmentType, setEnvironmentType] = useState<'trial' | 'beta' | 'api-partner' | 'client' | 'demo' | 'admin'>('trial');
    const [environmentStatus, setEnvironmentStatus] = useState<'active' | 'expired' | 'suspended' | 'cancelled'>('active');
    const [packageFeatures, setPackageFeatures] = useState<string[]>([]);
    const [activeFeatures, setActiveFeatures] = useState<string[]>([]);
    const [roleFeatures, setRoleFeatures] = useState<string[]>([]);
    const [accessTo, setAccessTo] = useState<string[]>([]);
    const [allowedFeatures, setAllowedFeatures] = useState<string[]>([]);
    const [allowedRights, setAllowedRights] = useState<string[]>([]);
    const [redirectTo, setRedirectTo] = useState<string | null>(() => {
        const encryptedValue = localStorage.getItem('redirect_to');
        return encryptedValue ? getDecryptedLocalStorageItem(encryptedValue) : null;
    });
    const [subscriptionIsTrial, setSubscriptionIsTrial] = useState<boolean | null>(null);
    const [subscriptionStartDate, setSubscriptionStartDate] = useState<string | null>(null);
    const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(null);
    const [subscriptionNoSepaMandate, setSubscriptionNoSepaMandate] = useState<boolean>(false);
    const [subscriptionIsProvisional, setSubscriptionIsProvisional] = useState<boolean>(false);
    const [packageIsViewOnly, setPackageIsViewOnly] = useState<boolean>(false);
    const [pageReloaded, setPageReloaded] = useState(false);
    const { setFormAlert, setErrorMessages } = useGlobalContext();
    const location = useLocation();

    // Tracks whether the session payload of a pinned tab was already requested
    const tabSessionRequested = useRef(false);

    // Set a csrf token in the browser
    useEffect(() => {
        setCsrfToken();
    }, []);

    // Set the login callbacks
    const loginCallbacks = {
        setIsLoggedIn,
        setUserHash,
        setUserDivision,
        setFirstName,
        setLastName,
        setEnvironmentHash,
        setEnvironmentType,
        setEnvironmentStatus,
        setPackageFeatures,
        setActiveFeatures,
        setRoleFeatures,
        setAccessTo,
        setAllowedFeatures,
        setAllowedRights,
        setRedirectTo,
        setSubscriptionIsTrial,
        setSubscriptionStartDate,
        setSubscriptionEndDate,
        setSubscriptionNoSepaMandate,
        setSubscriptionIsProvisional,
        setPackageIsViewOnly,
        setStatesLoaded
    };

    // Handle the login
    const handleLogin = async (
        email: string, 
        password: string, 
        navigator_language: string, 
        navigator_locale: string, 
        navigator_timezone: string | null, 
        utc_datetime: string,
        remember_login: boolean
    ): Promise<{status: string, error?: string}> => {
        const loginResult = await login({ 
            email, 
            password, 
            navigator_language, 
            navigator_locale, 
            navigator_timezone, 
            utc_datetime, 
            remember_login, 
            callbacks: loginCallbacks
        })
    
        if (loginResult.status === 'success') {
            // Set the redirect from the login response
            if (loginResult.redirect) {
                setRedirectTo(loginResult.redirect)
            }

            setFormAlert(null);
            setErrorMessages({});
            return { status: 'success' };

        // Pass the two factor state so the login form can redirect to the code screen
        } else if (loginResult.status === 'two_factor') {
            setFormAlert(null);
            setErrorMessages({});
            return { status: 'two_factor' };
        } else {

            // Redirect to the public payment page when the environment is blocked for payment
            if (loginResult.paymentUrl) {
                window.location.assign(loginResult.paymentUrl);
                return { status: 'payment_required' };
            }

            throw new Error(loginResult.error)
        }
    }

    // Handle the two factor code verification to complete the login
    const handleTwoFactorVerify = async (code: string): Promise<{status: string, error?: string}> => {

        // Get the navigator language, locale and timezone
        const navigator_language = getNavigatorLanguageOrFallback(navigator.language, 'language');
        const navigator_locale = getNavigatorLanguageOrFallback(navigator.language, 'locale');
        const navigator_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        // Get the current utc datetime
        const utc_datetime = convertLocalDateTimeToUTC(new Date(), navigator_locale, navigator_timezone);

        // Execute the two factor verification
        const verifyResult = await verifyTwoFactorCode({
            code,
            navigator_language,
            navigator_locale,
            navigator_timezone,
            utc_datetime,
            callbacks: loginCallbacks
        });

        if (verifyResult.status === 'success') {
            // Set the redirect from the verification response
            if (verifyResult.redirect) {
                setRedirectTo(verifyResult.redirect);
            }

            setFormAlert(null);
            setErrorMessages({});
            return { status: 'success' };
        } else {
            throw new Error(verifyResult.error)
        }
    }

    // Switch environment
    const handleSwitchEnvironment = async (
        environment_hash: string
    ): Promise<{status: string, error?: string}> => {

        // Get the current utc datetime
        const navigator_locale = getNavigatorLanguageOrFallback(navigator.language, 'locale');
        const navigator_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const utc_datetime = convertLocalDateTimeToUTC(new Date(), navigator_locale, navigator_timezone);

        // Execute the environment switch
        const switchResult = await switchEnvironmentRequest({
            environment_hash,
            utc_datetime,
            callbacks: loginCallbacks
        });

        if (switchResult.status === 'success') {
            // Reload the whole app on the redirect page, this clears all cached
            // data of the previous environment so no stale data is shown
            if (switchResult.redirect) {
                window.location.assign(`/${switchResult.redirect}`);
            } else {
                window.location.reload();
            }

            return { status: 'success' };
        } else {
            throw new Error('Failed switching environment');
        }
    };

    // Continiously renew allowed rights on page change when the no_user or staff_user flag is set
    useEffect(() => {
        if (Array.isArray(allowedRights) && allowedRights.length > 0 && (allowedRights.includes('no_user') || allowedRights.includes('staff_user'))) {
            const fetchAllowedRights = async () => {
                try {
                    // Fetch the allowed rights
                    const fetchedRights = await fetchData({ apiUrl: 'refresh_allowed_rights', handleLogout });

                    // Set the refreshed allowed rights in the context
                    setAllowedRights(fetchedRights);

                    // Encrypt the refreshed rights and put them in the localStorage
                    const encryptedRights = encryptData(JSON.stringify(fetchedRights));
                    localStorage.setItem('allowed_rights', encryptedRights)
                } catch (error: any) {
                    console.error(error);
                }
            };
            fetchAllowedRights();
        }
    }, [location])

    // Set page reloaded flag on page reload
    useEffect(() => {
        setPageReloaded(true);
    }, [])

    // Refresh states from local storage on hard page reload
    useEffect(() => {
        if (currentSession === true) {
            // Refresh the following states from the local storage
            const refreshStates = () => refreshStatesFromLocalStorage({
                setUserHash, setUserDivision, setFirstName, setLastName, setEnvironmentHash, setEnvironmentType, setEnvironmentStatus, setPackageFeatures,
                setActiveFeatures, setRoleFeatures, setAccessTo, setAllowedFeatures, setAllowedRights, setRedirectTo,
                setSubscriptionIsTrial, setSubscriptionStartDate, setSubscriptionEndDate, setSubscriptionNoSepaMandate, setSubscriptionIsProvisional,
                setPackageIsViewOnly
            });

            // Determine whether this tab is pinned to another environment than the default session
            const tabEnvironmentHash = getTabEnvironmentHash();
            const defaultEnvironmentHash = getDefaultEnvironmentHash();
            const tabIsPinnedElsewhere = tabEnvironmentHash !== null && tabEnvironmentHash !== defaultEnvironmentHash;

            // Load the session payload of the pinned environment instead of the default session
            if (tabIsPinnedElsewhere) {
                if (tabSessionRequested.current) return;
                tabSessionRequested.current = true;

                fetchSessionData().then((responseData) => {
                    loadTabSessionData({ responseData, callbacks: loginCallbacks });
                }).catch((error) => {
                    // Log out when the session itself is gone
                    if (axios.isAxiosError(error) && error.response && [401, 403].includes(error.response.status)) {
                        handleLogout({ showSessionAlert: true });
                        return;
                    }

                    // Fall back to the default session so the tab keeps working
                    console.error('Loading the pinned environment failed, falling back to the default environment', error);
                    clearTabScope();
                    refreshStates();
                    setStatesLoaded(true);
                });
                return;
            }

            // Refresh the states from the default session
            refreshStates();

            // Pin this tab to the default environment so a switch in another tab never moves it
            const defaultUserHash = getDecryptedLocalStorageItem('user_hash');
            if (defaultEnvironmentHash && defaultUserHash) {
                setTabScope(defaultEnvironmentHash, defaultUserHash);
            }

            // Set states loaded to true
            setStatesLoaded(true);
        }
    }, [setUserHash, setUserDivision, setFirstName, setLastName, setEnvironmentHash, setPackageFeatures, setEnvironmentType,
        setEnvironmentStatus, setActiveFeatures, setRoleFeatures, setAccessTo, setAllowedFeatures, setAllowedRights, setRedirectTo, 
        setSubscriptionIsTrial, setSubscriptionStartDate, setSubscriptionEndDate, setSubscriptionNoSepaMandate, setSubscriptionIsProvisional, setPackageIsViewOnly, pageReloaded]);

    // Open the single multiplexed updates websocket while a session is active
    useEffect(() => {
        if (currentSession != null) connectUpdatesSocket();

        // Close the websocket when the session ends or the provider unmounts
        return () => {
            disconnectUpdatesSocket();
        };
    }, [currentSession]);

    // Receive package and features updates over the updates websocket
    const { message: featuresUpdateMessage } = useSocketMessage(['features_update']);
    useEffect(() => {
        if (featuresUpdateMessage?.type === 'features_update') {
            // Only the tab in the default environment updates the local storage,
            // a tab pinned elsewhere would overwrite the default session of new tabs
            const persistToLocalStorage = isTabOnDefaultEnvironment();

            // Update the package features state and put it encrypted in the local storage
            setPackageFeatures(featuresUpdateMessage.package_features)
            const encryptedPackageFeatures = encryptData(JSON.stringify(featuresUpdateMessage.package_features));
            if (persistToLocalStorage) localStorage.setItem('package_features', encryptedPackageFeatures);

            // Update the active features state and put it encrypted in the local storage
            setActiveFeatures(featuresUpdateMessage.active_features)
            const encryptedActiveFeatures = encryptData(JSON.stringify(featuresUpdateMessage.active_features));
            if (persistToLocalStorage) localStorage.setItem('active_features', encryptedActiveFeatures);

            // Update the role features state and put it encrypted in the local storage
            setRoleFeatures(featuresUpdateMessage.role_features)
            const encryptedRoleFeatures = encryptData(JSON.stringify(featuresUpdateMessage.role_features));
            if (persistToLocalStorage) localStorage.setItem('role_features', encryptedRoleFeatures);

            // Update the view only package state and put it encrypted in the local storage
            if (typeof featuresUpdateMessage.view_only_package === 'boolean') {
                setPackageIsViewOnly(featuresUpdateMessage.view_only_package)
                if (persistToLocalStorage) localStorage.setItem('view_only_package', encryptData(JSON.stringify(featuresUpdateMessage.view_only_package)));
            }
        }
    }, [featuresUpdateMessage, setPackageFeatures, setActiveFeatures, setRoleFeatures, setPackageIsViewOnly]);

    // Receive role updates over the updates websocket
    const { message: roleUpdateMessage } = useSocketMessage(['role_update']);
    useEffect(() => {
        if (roleUpdateMessage?.type === 'role_update') {
            // Update the access to state and put it encrypted in the local storage
            setAccessTo(roleUpdateMessage.access_to)
            const encryptedAccessTo = encryptData(JSON.stringify(roleUpdateMessage.access_to));
            localStorage.setItem('access_to', encryptedAccessTo);

            // Update the role features state and put it encrypted in the local storage
            setRoleFeatures(roleUpdateMessage.role_features)
            const encryptedRoleFeatures = encryptData(JSON.stringify(roleUpdateMessage.role_features));
            localStorage.setItem('role_features', encryptedRoleFeatures);

            // Update the allowed features state and put it encrypted in the local storage
            setAllowedFeatures(roleUpdateMessage.allowed_features)
            const encryptedAllowedFeatures = encryptData(JSON.stringify(roleUpdateMessage.allowed_features));
            localStorage.setItem('allowed_features', encryptedAllowedFeatures);

            // Update the allowed rights state and put it encrypted in the local storage
            setAllowedRights(roleUpdateMessage.allowed_rights)
            const encryptedAllowedRights = encryptData(JSON.stringify(roleUpdateMessage.allowed_rights));
            localStorage.setItem('allowed_rights', encryptedAllowedRights);
        }
    }, [roleUpdateMessage, setAccessTo, setRoleFeatures, setAllowedFeatures, setAllowedRights]);

    // Handle the logout (optionally serverside), delete the local storage items and redirect to the login page
    const handleLogout = async ({ logoutFromServer = false, showSessionAlert = false }: { logoutFromServer?: boolean, showSessionAlert?: boolean } = {}) => {
        // Close the updates websocket before ending the session
        disconnectUpdatesSocket();

        await logout({
            logoutFromServer,
            showSessionAlert,
            callbacks: { setIsLoggedIn, setRedirectTo, setFormAlert }
        });
    }

    return (
        <AuthenticationContext.Provider 
            value={{ currentSession, statesLoaded, setStatesLoaded, isLoggedIn, setIsLoggedIn, userHash, setUserHash,
                userDivision, setUserDivision, firstName, setFirstName, lastName, setLastName, environmentHash, setEnvironmentHash,
                environmentType, setEnvironmentType, environmentStatus, setEnvironmentStatus, packageFeatures, setPackageFeatures, 
                activeFeatures, setActiveFeatures, roleFeatures, setRoleFeatures, accessTo, setAccessTo, allowedFeatures, setAllowedFeatures, allowedRights, 
                setAllowedRights, handleLogin, handleTwoFactorVerify, handleSwitchEnvironment, handleLogout, redirectTo, setRedirectTo, subscriptionIsTrial, setSubscriptionIsTrial,
                subscriptionStartDate, setSubscriptionStartDate, subscriptionEndDate, setSubscriptionEndDate,
                subscriptionNoSepaMandate, setSubscriptionNoSepaMandate, subscriptionIsProvisional, setSubscriptionIsProvisional,
                packageIsViewOnly, setPackageIsViewOnly }}>
            {children}
        </AuthenticationContext.Provider>
    );
};

// Create a custom hook for the authentication context
export const useAuthContext = () => {
    return useContext(AuthenticationContext);
}